import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';

interface AuditRow {
  id: string;
  timestamp: string;
  userId: string;
  action: string;
  resource: string;
  resourceId: string | null;
  flagType: string | null;
  resolution: string | null;
  overrideReason: string | null;
}

const FLAG_TYPES = [
  'PROFANITY',
  'COMPETITOR_MENTION',
  'VERBAL_PRICING',
  'DISCRIMINATORY_LANGUAGE',
  'VIN_MISMATCH',
  'PII_DETECTED',
  'NEGATIVE_SENTIMENT',
  'LOW_VQS_SCORE',
  'EXCESSIVE_FILLERS',
  'REQUIRED_PHRASE_MISSING',
];

const ComplianceAuditTab: React.FC = () => {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [loading, setLoading] = useState(false);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [flagType, setFlagType] = useState('');
  const [action, setAction] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (flagType) params.set('flagType', flagType);
      if (action) params.set('action', action);
      params.set('page', String(page));
      params.set('perPage', String(perPage));
      const res = await api.get(`/reports/compliance-audit?${params.toString()}`);
      setRows(res.data.data ?? []);
      setTotal(res.data.meta?.total ?? 0);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, flagType, action, page, perPage]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    if (!rows.length) return;
    const header = [
      'timestamp',
      'action',
      'resource',
      'resourceId',
      'flagType',
      'userId',
      'resolution',
      'overrideReason',
    ];
    const escape = (v: string | null) => {
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const lines = [header.join(',')];
    for (const r of rows) {
      lines.push(
        [
          r.timestamp,
          r.action,
          r.resource,
          r.resourceId,
          r.flagType,
          r.userId,
          r.resolution,
          r.overrideReason,
        ]
          .map(escape)
          .join(','),
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 block mb-1.5">
              From
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setPage(1);
                setDateFrom(e.target.value);
              }}
              className="h-10 w-full px-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-sm font-medium text-slate-600 dark:text-slate-400 outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 block mb-1.5">
              To
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setPage(1);
                setDateTo(e.target.value);
              }}
              className="h-10 w-full px-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-sm font-medium text-slate-600 dark:text-slate-400 outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 block mb-1.5">
              Flag Type
            </label>
            <select
              value={flagType}
              onChange={(e) => {
                setPage(1);
                setFlagType(e.target.value);
              }}
              className="h-10 w-full px-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-sm font-medium text-slate-600 dark:text-slate-400 outline-none focus:border-primary"
            >
              <option value="">All</option>
              {FLAG_TYPES.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 block mb-1.5">
              Action
            </label>
            <input
              type="text"
              value={action}
              onChange={(e) => {
                setPage(1);
                setAction(e.target.value);
              }}
              placeholder="e.g. COMPLIANCE_FLAG_RESOLVED"
              className="h-10 w-full px-3 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-sm font-medium text-slate-600 dark:text-slate-400 outline-none focus:border-primary"
            />
          </div>
          <div className="flex items-end">
            <button
              type="button"
              onClick={exportCsv}
              disabled={rows.length === 0}
              className="h-10 w-full px-4 rounded-xl bg-primary text-white text-[10px] font-black uppercase tracking-widest hover:bg-primary/90 transition-all disabled:opacity-40 flex items-center justify-center gap-2"
            >
              <span className="material-symbols-outlined text-[16px]">download</span>
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Rows */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-20 flex flex-col items-center">
            <span className="material-symbols-outlined text-[48px] text-slate-300 dark:text-slate-700">
              fact_check
            </span>
            <p className="text-slate-400 dark:text-slate-600 text-sm font-medium mt-2">
              No audit entries match your filters.
            </p>
          </div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20">
                  {['Timestamp', 'Action', 'Resource', 'Flag Type', 'Resolution', 'Override Reason'].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors"
                  >
                    <td className="px-4 py-3 text-[11px] text-slate-500 dark:text-slate-400 whitespace-nowrap">
                      {new Date(r.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-bold text-slate-900 dark:text-white text-[11px]">
                      {r.action}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-slate-500 dark:text-slate-400">
                      {r.resource}
                    </td>
                    <td className="px-4 py-3">
                      {r.flagType ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest bg-amber-50 dark:bg-amber-950/30 text-amber-500">
                          {r.flagType}
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-slate-500 dark:text-slate-400">
                      {r.resolution ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-slate-500 dark:text-slate-400 max-w-xs truncate">
                      {r.overrideReason ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 dark:border-slate-800">
              <span className="text-[11px] font-bold text-slate-400">
                Page {page} of {totalPages} — {total} total entries
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-8 px-3 rounded-lg text-[10px] font-black uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 disabled:opacity-40"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="h-8 px-3 rounded-lg text-[10px] font-black uppercase tracking-widest bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ComplianceAuditTab;
